import { Box, Stack, Text } from '@mantine/core'
import { IconButton } from '@mui/material'
import {
	BOOK_UNIVINI_ICON_BRAND,
	BOOK_UNIVINI_ICON_MUTED,
	BOOK_UNIVINI_ICON_SECONDARY,
	CloseFullIcon,
	DocumentDownloadIcon,
	MagicPenIcon,
} from 'src/components/BookUniVini/BookUniViniFileIcons'
import touchClasses from 'src/components/BookUniVini/BookUniViniTouch.module.scss'
import { getVariantLabel } from 'src/utils/bookAudioDialects'
import { formatFileSize } from 'src/utils/formatFileSize'
import { downloadFileUrl, getFilenameFromUrl } from 'src/utils/fileUrl'
import { BookAudioItem } from 'src/interfaces'

interface AudioFileCardProps {
	audio: BookAudioItem
	onRemove?: () => void
	onRegenerate?: () => void
	regenerating?: boolean
}

function AudioFileCard({
	audio,
	onRemove,
	onRegenerate,
	regenerating = false,
}: AudioFileCardProps) {
	const filename = audio.filename || getFilenameFromUrl(audio.url)
	const languageLabel = getVariantLabel(audio.language_variant, audio.language)
	const hasActions = Boolean(onRemove || onRegenerate || audio.url)

	return (
		<Box
			sx={{
				border: '1px solid #e6ebe7',
				borderRadius: 10,
				padding: 12,
				position: 'relative',
				background: '#fff',
			}}
		>
			{hasActions && (
				<Box sx={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 1.5 }}>
					{onRemove && (
						<IconButton
							onClick={onRemove}
							size="small"
							disabled={regenerating}
							aria-label="Remove audio"
							className={touchClasses.touchIconButton}
							sx={{ color: BOOK_UNIVINI_ICON_MUTED, p: 0.5 }}
						>
							<CloseFullIcon size={20} />
						</IconButton>
					)}
					{onRegenerate && (
						<IconButton
							onClick={onRegenerate}
							size="small"
							disabled={regenerating}
							aria-label="Regenerate audio"
							className={touchClasses.touchIconButton}
							sx={{ color: BOOK_UNIVINI_ICON_SECONDARY, p: 0.5 }}
						>
							<MagicPenIcon size={20} />
						</IconButton>
					)}
					{audio.url && (
						<IconButton
							onClick={() => downloadFileUrl(audio.url!)}
							size="small"
							aria-label="Download audio"
							className={touchClasses.touchIconButton}
							sx={{ color: BOOK_UNIVINI_ICON_BRAND, p: 0.5 }}
						>
							<DocumentDownloadIcon size={20} />
						</IconButton>
					)}
				</Box>
			)}

			<Stack spacing={10} pr={hasActions ? 88 : 0}>
				<Stack spacing={4}>
					<Text size="sm" weight={600} lineClamp={1}>
						{filename}
					</Text>
					<Text size="xs" color="dimmed">
						{formatFileSize(audio.file_size)} · {languageLabel}
					</Text>
				</Stack>

				{audio.url ? (
					<audio
						controls
						src={audio.url}
						className={touchClasses.touchAudioPlayer}
					>
						Your browser does not support the audio element.
					</audio>
				) : audio.status && /pending/i.test(audio.status) ? (
					<Text size="sm" color="dimmed">
						Processing...
					</Text>
				) : (
					<Text size="sm" color="dimmed">
						No audio URL
					</Text>
				)}
			</Stack>
		</Box>
	)
}

export default AudioFileCard
